import type { BeatLineViteConfig } from '../api/types';

type AnyWindow = Window & Record<string, any>;

const C40_VERSION = '2.17.2-c50-restore-working-native-auth';

function wAny(): AnyWindow { return window as AnyWindow; }

function isNativeApp(config: BeatLineViteConfig): boolean {
  const w = wAny();
  return !!(
    config.nativeApp ||
    config.entryContext === 'app' ||
    w.BeatLineNative ||
    w.Capacitor ||
    /(?:^|[?&])native_app=1(?:&|$)/.test(window.location.search) ||
    window.location.protocol === 'capacitor:' ||
    window.location.protocol === 'file:'
  );
}

function latestState(): Record<string, any> | null {
  const w = wAny();
  return (w.__beatlineTsLastState || w.beatlineState || w.state || null) as Record<string, any> | null;
}

function isStartOverlayVisible(): boolean {
  const overlay = document.getElementById('start-overlay') as HTMLElement | null;
  if (!overlay) return false;
  if (overlay.style.display && overlay.style.display !== 'none') return true;
  try {
    return getComputedStyle(overlay).display !== 'none' && getComputedStyle(overlay).visibility !== 'hidden';
  } catch (_) {
    return false;
  }
}

function selectedRadio(name: string, fallback: string): string {
  return document.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`)?.value || fallback;
}

function selectedCountFromControls(): number {
  const n = parseInt(selectedRadio('p_count', '1'), 10);
  return Number.isFinite(n) && n >= 1 && n <= 4 ? n : 1;
}

function selectedSoloFromControls(): string {
  return selectedRadio('solo_mode', 'classic') || 'classic';
}

function gameScreenIsActive(): boolean {
  const game = document.getElementById('game-screen') as HTMLElement | null;
  if (!game) return false;
  try {
    return getComputedStyle(game).display !== 'none' && getComputedStyle(game).visibility !== 'hidden';
  } catch (_) {
    return !!game.offsetParent;
  }
}

function gameHasStarted(): boolean {
  const state = latestState();
  if (state && (state.activeCard || Array.isArray(state.deck) || state.mode || state.playerCount)) return gameScreenIsActive();
  const deck = document.getElementById('deck-zone') as HTMLElement | null;
  const hasTimelineCards = [1, 2, 3, 4].some((slot) => {
    const tl = document.getElementById(`timeline-p${slot}`);
    return !!tl && tl.children.length > 0;
  });
  return gameScreenIsActive() && (!!deck?.children.length || hasTimelineCards || !isStartOverlayVisible());
}

function selectedPlayerCount(): number {
  const w = wAny();
  const forced = w.__beatlineForcedPlayerCount;
  const forcedUntil = parseInt(String(w.__beatlineForcedPlayerCountUntil || 0), 10) || 0;
  if (Number.isFinite(forced) && forced > 0 && forced <= 4 && Date.now() < forcedUntil) return forced;
  // C40: een forced zero is alleen een leave/start-cleanup marker. Tijdens een actief spel
  // mag de verborgen start-radio nooit een lopend 2/3/4-spel terug naar 1 speler drukken.
  if (Number.isFinite(forced) && forced === 0 && Date.now() < forcedUntil && !gameHasStarted()) return selectedCountFromControls();

  const controlCount = selectedCountFromControls();
  const activeGame = gameScreenIsActive() && !isStartOverlayVisible();

  if (!activeGame) return controlCount;

  const state = latestState();
  const fromState = parseInt(String(state?.playerCount ?? ''), 10);
  if (Number.isFinite(fromState) && fromState >= 1 && fromState <= 4) return fromState;

  const next = parseInt(String(w.__beatlineNextGamePlayerCount || ''), 10);
  if (Number.isFinite(next) && next >= 1 && next <= 4) return next;

  return controlCount;
}

function selectedSoloMode(): string {
  const state = latestState();
  if (state?.speedrun) return 'speedrun';
  if (state?.timeAttack) return 'timeattack';
  if (state?.survival) return 'survival';
  if (state?.perfectOrder) return 'perfect';

  const w = wAny();
  if (typeof w.__beatlineNextGameSoloMode === 'string' && w.__beatlineNextGameSoloMode) return w.__beatlineNextGameSoloMode;
  return selectedSoloFromControls();
}

function installHardCssGuards(): void {
  if (document.getElementById('beatline-c27-hard-app-state-style')) return;
  const style = document.createElement('style');
  style.id = 'beatline-c27-hard-app-state-style';
  style.textContent = `
    #beatline-app[data-player-count="1"] #zone-p2,
    #beatline-app[data-player-count="1"] #zone-p3,
    #beatline-app[data-player-count="1"] #zone-p4,
    #beatline-app[data-player-count="2"] #zone-p3,
    #beatline-app[data-player-count="2"] #zone-p4,
    #beatline-app[data-player-count="3"] #zone-p4,
    #beatline-app[data-player-count="1"] #stats-p2,
    #beatline-app[data-player-count="1"] #stats-p3,
    #beatline-app[data-player-count="1"] #stats-p4,
    #beatline-app[data-player-count="2"] #stats-p3,
    #beatline-app[data-player-count="2"] #stats-p4,
    #beatline-app[data-player-count="3"] #stats-p4 {
      display: none !important;
      visibility: hidden !important;
    }
    #beatline-app[data-player-count="1"] #zone-p1,
    #beatline-app[data-player-count="2"] #zone-p1,
    #beatline-app[data-player-count="2"] #zone-p2,
    #beatline-app[data-player-count="3"] #zone-p1,
    #beatline-app[data-player-count="3"] #zone-p2,
    #beatline-app[data-player-count="3"] #zone-p3,
    #beatline-app[data-player-count="4"] #zone-p1,
    #beatline-app[data-player-count="4"] #zone-p2,
    #beatline-app[data-player-count="4"] #zone-p3,
    #beatline-app[data-player-count="4"] #zone-p4 {
      display: flex !important;
      visibility: visible !important;
    }
    #beatline-app[data-player-count="1"]:not([data-solo-mode="speedrun"]) #active-score-panel,
    body.beatline-force-one-timeline:not(.beatline-force-speedrun) #active-score-panel,
    html.beatline-force-one-timeline:not(.beatline-force-speedrun) #active-score-panel,
    #beatline-app.beatline-singleplayer:not(.beatline-speedrun-mode) #active-score-panel {
      display: none !important;
      visibility: hidden !important;
    }
    #beatline-app[data-player-count="2"] #active-score-panel,
    #beatline-app[data-player-count="3"] #active-score-panel,
    #beatline-app[data-player-count="4"] #active-score-panel,
    #beatline-app.beatline-multiplayer #active-score-panel {
      display: grid !important;
      visibility: visible !important;
    }
    body.beatline-force-one-timeline #zone-p2,
    body.beatline-force-one-timeline #zone-p3,
    body.beatline-force-one-timeline #zone-p4,
    body.beatline-force-one-timeline #stats-p2,
    body.beatline-force-one-timeline #stats-p3,
    body.beatline-force-one-timeline #stats-p4,
    html.beatline-force-one-timeline #zone-p2,
    html.beatline-force-one-timeline #zone-p3,
    html.beatline-force-one-timeline #zone-p4,
    html.beatline-force-one-timeline #stats-p2,
    html.beatline-force-one-timeline #stats-p3,
    html.beatline-force-one-timeline #stats-p4,
    #beatline-app.beatline-singleplayer #zone-p2,
    #beatline-app.beatline-singleplayer #zone-p3,
    #beatline-app.beatline-singleplayer #zone-p4 {
      display: none !important;
      visibility: hidden !important;
    }
    #beatline-app[data-player-count="1"][data-solo-mode="speedrun"],
    body.beatline-force-one-timeline.beatline-force-speedrun #active-score-panel {
      visibility: visible !important;
    }
  `;
  document.head.appendChild(style);
}

function setForcedPlayerCount(count: number, ms = 6000): void {
  const w = wAny();
  w.__beatlineForcedPlayerCount = Math.max(0, Math.min(4, count || 0));
  w.__beatlineForcedPlayerCountUntil = Date.now() + ms;
}

function applyModeAttributes(reason: string): void {
  const app = document.getElementById('beatline-app') as HTMLElement | null;
  if (!app) return;
  const count = selectedPlayerCount();
  const solo = selectedSoloMode();
  app.dataset.playerCount = String(count);
  app.dataset.soloMode = count <= 1 ? solo : 'multi';
  app.classList.toggle('beatline-singleplayer', count <= 1);
  app.classList.toggle('beatline-multiplayer', count > 1);
  app.classList.toggle('beatline-speedrun-mode', count <= 1 && solo === 'speedrun');
  if (wAny().BEATLINE_VITE_DEBUG) {
    try { console.info('[BeatLine Vite C40] mode attrs', { reason, count, solo, statePc: latestState()?.playerCount }); } catch (_) {}
  }
}

function controlCountIsOne(): boolean {
  return selectedCountFromControls() === 1;
}

function currentSoloModeFromControlsOrState(): string {
  return selectedSoloFromControls() || selectedSoloMode() || 'classic';
}

function shouldForceOneTimelineNow(): boolean {
  const activeGame = gameScreenIsActive() && !isStartOverlayVisible();
  if (activeGame) return selectedPlayerCount() === 1;
  return controlCountIsOne() || selectedPlayerCount() === 1;
}

function syncForceOneTimelineClasses(reason: string): void {
  const shouldForce = shouldForceOneTimelineNow();
  const solo = currentSoloModeFromControlsOrState();
  document.body?.classList.toggle('beatline-force-one-timeline', shouldForce);
  document.body?.classList.toggle('beatline-force-speedrun', shouldForce && solo === 'speedrun');
  document.documentElement.classList.toggle('beatline-force-one-timeline', shouldForce);
  document.documentElement.classList.toggle('beatline-force-speedrun', shouldForce && solo === 'speedrun');
  const app = document.getElementById('beatline-app') as HTMLElement | null;
  if (app && shouldForce) {
    app.dataset.playerCount = '1';
    app.dataset.soloMode = solo;
    app.classList.add('beatline-singleplayer');
    app.classList.remove('beatline-multiplayer');
  }
  if (wAny().BEATLINE_VITE_DEBUG) {
    try { console.info('[BeatLine Vite C40] one timeline classes', { reason, shouldForce, solo, controlCount: selectedCountFromControls(), selectedCount: selectedPlayerCount(), statePc: latestState()?.playerCount }); } catch (_) {}
  }
}

function hideInactiveSinglePlayerDom(reason: string): void {
  if (!shouldForceOneTimelineNow()) return;
  syncForceOneTimelineClasses(reason);
  for (let slot = 2; slot <= 4; slot += 1) resetTimelineZone(slot, true);
  const zone1 = document.getElementById('zone-p1') as HTMLElement | null;
  if (zone1) {
    zone1.style.setProperty('display', 'flex', 'important');
    zone1.style.setProperty('visibility', 'visible', 'important');
    zone1.removeAttribute('hidden');
    zone1.setAttribute('aria-hidden', 'false');
    zone1.style.order = '1';
  }
  const solo = currentSoloModeFromControlsOrState();
  const panel = document.getElementById('active-score-panel') as HTMLElement | null;
  if (panel && solo !== 'speedrun') {
    panel.style.setProperty('display', 'none', 'important');
    panel.style.setProperty('visibility', 'hidden', 'important');
    panel.setAttribute('hidden', 'hidden');
    panel.setAttribute('aria-hidden', 'true');
  }
}

function resetTimelineZone(slot: number, hide: boolean): void {
  const zone = document.getElementById(`zone-p${slot}`) as HTMLElement | null;
  const timeline = document.getElementById(`timeline-p${slot}`) as HTMLElement | null;
  const stats = document.getElementById(`stats-p${slot}`) as HTMLElement | null;
  if (timeline && hide) {
    timeline.innerHTML = '';
    timeline.classList.remove('timeline-compact', 'sortable-chosen', 'sortable-ghost', 'sortable-drag', 'has-pending');
  }
  if (zone) {
    if (hide) {
      zone.style.setProperty('display', 'none', 'important');
      zone.style.setProperty('visibility', 'hidden', 'important');
      zone.setAttribute('hidden', 'hidden');
      zone.setAttribute('aria-hidden', 'true');
    } else {
      zone.style.setProperty('display', 'flex', 'important');
      zone.style.setProperty('visibility', 'visible', 'important');
      zone.removeAttribute('hidden');
      zone.setAttribute('aria-hidden', 'false');
    }
    if (hide) {
      zone.style.order = '';
      zone.classList.remove('zone-active', 'zone-focus', 'zone-compact', 'zone-dimmed', 'is-active', 'has-pending', 'perfect-correct', 'perfect-wrong');
    }
  }
  if (stats && hide) {
    stats.style.setProperty('display', 'none', 'important');
    stats.style.setProperty('visibility', 'hidden', 'important');
    stats.setAttribute('hidden', 'hidden');
    stats.setAttribute('aria-hidden', 'true');
    stats.classList.remove('visible', 'active', 'is-active');
    const score = stats.querySelector('.score');
    const tokens = stats.querySelector('.tokens');
    if (score) score.textContent = '0';
    if (tokens) tokens.textContent = '0';
  } else if (stats && !hide) {
    // beatline_source_stats_count_fix_v2
    // Source fix: visible player stats must not keep stale hidden state.
    // Mobile header-activefirst may still keep non-active stats hidden for chips.
    const headerForStatsCountFix = document.querySelector('.game-header') as HTMLElement | null;
    const activeFirstForStatsCountFix = !!headerForStatsCountFix?.classList.contains('header-activefirst');
    const mayUnhideStatsCountFix =
      !activeFirstForStatsCountFix ||
      stats.classList.contains('active-turn') ||
      stats.classList.contains('header-main');

    if (mayUnhideStatsCountFix) {
      stats.hidden = false;
      stats.removeAttribute('hidden');
      stats.setAttribute('aria-hidden', 'false');

      if (stats.style.getPropertyValue('display') === 'none') {
        stats.style.removeProperty('display');
      }

      if (stats.style.getPropertyValue('visibility') === 'hidden') {
        stats.style.removeProperty('visibility');
      }

      stats.classList.add('visible');
    }
  }
}

function enforceTimelineCount(count: number, reason: string): void {
  installHardCssGuards();
  syncForceOneTimelineClasses(reason);
  applyModeAttributes(reason);
  const controlsForceOne = shouldForceOneTimelineNow();

  // beatline_source_stats_count_fix_v2
  // Use the real selected/dataset playerCount before the incoming count argument.
  const appForTimelineCount = document.getElementById('beatline-app') as HTMLElement | null;
  const datasetCountForTimeline = parseInt(String(appForTimelineCount?.dataset?.playerCount || ''), 10) || 0;
  const controlsCountForTimeline = selectedPlayerCount() || selectedCountFromControls();
  const argumentCountForTimeline = parseInt(String(count || 0), 10) || 0;
  const rawCount = controlsCountForTimeline || datasetCountForTimeline || argumentCountForTimeline;
  const safeCount = controlsForceOne ? 1 : Math.max(1, Math.min(4, rawCount));
  for (let slot = 1; slot <= 4; slot += 1) {
    const hide = slot > safeCount;
    resetTimelineZone(slot, hide);
  }
  hideInactiveSinglePlayerDom(reason);
}

function enforceActiveScorePanelForCurrentMode(reason: string): void {
  installHardCssGuards();
  syncForceOneTimelineClasses(reason);
  applyModeAttributes(reason);
  const panel = document.getElementById('active-score-panel') as HTMLElement | null;
  if (!panel) return;
  const app = document.getElementById('beatline-app') as HTMLElement | null;
  const state = latestState();
  const stateCount = parseInt(String(state?.playerCount ?? ''), 10);
  const dataCount = parseInt(String(app?.dataset.playerCount ?? ''), 10);
  const playerCount = selectedPlayerCount();
  const isMultiplayer = playerCount > 1 || stateCount > 1 || dataCount > 1 || app?.classList.contains('beatline-multiplayer');

  // C40: multiplayer gebruikt het active-score-panel als normale score/target-indicator.
  // De solo-radio kan nog op classic/survival staan, maar mag een actief 2/3/4-spel niet verbergen.
  if (isMultiplayer) {
    // beatline_score_panel_tablet_fix_v1_ts — only write DOM if state differs (prevents 350ms flicker)
    const _curD = panel.style.getPropertyValue('display');
    const _curV = panel.style.getPropertyValue('visibility');
    if (_curD !== 'grid' || _curV === 'hidden' || panel.hasAttribute('hidden')) {
      panel.removeAttribute('hidden');
      panel.setAttribute('aria-hidden', 'false');
      panel.style.setProperty('display', 'grid', 'important');
      panel.style.setProperty('visibility', 'visible', 'important');
    }
    return;
  }

  const soloMode = selectedSoloMode();
  const isSingleSpeedrun = playerCount <= 1 && soloMode === 'speedrun';
  const hideSingleSpecial = !!(state?.perfectOrder || state?.timeAttack || state?.survival || soloMode === 'perfect' || soloMode === 'timeattack' || soloMode === 'survival' || soloMode === 'classic');
  if (!isSingleSpeedrun || hideSingleSpecial) {
    const _d2 = panel.style.getPropertyValue('display');
    const _v2 = panel.style.getPropertyValue('visibility');
    if (_d2 !== 'none' || _v2 !== 'hidden' || !panel.hasAttribute('hidden')) {
      panel.style.setProperty('display', 'none', 'important');
      panel.style.setProperty('visibility', 'hidden', 'important');
      panel.setAttribute('hidden', 'hidden');
      panel.setAttribute('aria-hidden', 'true');
    }
  } else {
    const _d3 = panel.style.getPropertyValue('display');
    const _v3 = panel.style.getPropertyValue('visibility');
    if (_d3 !== 'grid' || _v3 === 'hidden' || panel.hasAttribute('hidden')) {
      panel.removeAttribute('hidden');
      panel.setAttribute('aria-hidden', 'false');
      panel.style.setProperty('display', 'grid', 'important');
      panel.style.setProperty('visibility', 'visible', 'important');
    }
  }
}

function cleanupStartState(reason: string): void {
  if (!isStartOverlayVisible() || gameHasStarted()) return;
  // C33: keep player zones alone here. The selected player count is restored when a game starts.
  applyModeAttributes(`${reason}-keep-selected-count`);
  const deck = document.getElementById('deck-zone') as HTMLElement | null;
  if (deck) {
    deck.innerHTML = '';
    deck.classList.remove('has-card', 'card-active', 'is-active', 'playing', 'paused');
  }
  const centerIntervention = document.getElementById('center-intervention') as HTMLElement | null;
  if (centerIntervention) {
    if (centerIntervention.contains(document.activeElement)) (document.activeElement as HTMLElement | null)?.blur?.();
    centerIntervention.style.display = 'none';
    centerIntervention.setAttribute('aria-hidden', 'true');
    centerIntervention.classList.remove('is-open', 'is-active');
  }
}

function captureNextStartSelection(): void {
  const w = wAny();
  const count = selectedCountFromControls();
  const solo = selectedSoloFromControls();
  w.__beatlineNextGamePlayerCount = count;
  w.__beatlineNextGameSoloMode = solo;
  setForcedPlayerCount(count, 9000);
  applyModeAttributes('capture-next-start-selection');
  if (count === 1) hideInactiveSinglePlayerDom('capture-next-start-selection');
}

function installLeaveResetHardening(config: BeatLineViteConfig): void {
  const w = wAny();
  if (w.__beatlineC27LeaveResetHardening) return;
  w.__beatlineC27LeaveResetHardening = true;
  installHardCssGuards();
  applyModeAttributes('install');

  const originalReset = typeof w.beatlineTsResetBoardForNewGame === 'function' ? w.beatlineTsResetBoardForNewGame : null;
  if (originalReset && !originalReset.__c27Wrapped) {
    const wrapped = function(reason = 'reset') {
      const result = originalReset(reason);
      const r = String(reason || 'reset');
      if (r.includes('leave')) {
        setForcedPlayerCount(0, 1800);
        [0, 40, 120, 320, 800].forEach((ms) => window.setTimeout(() => cleanupStartState(`reset-${r}-${ms}`), ms));
      }
      return result;
    };
    wrapped.__c27Wrapped = true;
    w.beatlineTsResetBoardForNewGame = wrapped;
  }

  const originalInit = typeof w.initGame === 'function' ? w.initGame : null;
  if (originalInit && !originalInit.__c27Wrapped) {
    const wrappedInit = async function(this: any, ...args: any[]) {
      captureNextStartSelection();
      try { if (typeof w.beatlineTsResetBoardForNewGame === 'function') w.beatlineTsResetBoardForNewGame('pre-init-c27'); } catch (_) {}
      const countBefore = selectedPlayerCount();
      const result = await originalInit.apply(this, args);
      [0, 40, 100, 240, 520, 1100, 1800].forEach((ms) => window.setTimeout(() => {
        const count = selectedPlayerCount() || countBefore;
        enforceTimelineCount(count, `post-init-c27-${ms}`);
        enforceActiveScorePanelForCurrentMode(`post-init-c27-${ms}`);
      }, ms));
      return result;
    };
    wrappedInit.__c27Wrapped = true;
    w.initGame = wrappedInit;
  }

  const originalUpdateUI = typeof w.beatlineTsUpdateUI === 'function' ? w.beatlineTsUpdateUI : null;
  if (originalUpdateUI && !(originalUpdateUI as any).__c27Wrapped) {
    const wrappedUpdateUI = function(this: any, ...args: any[]) {
      const result = originalUpdateUI.apply(this, args);
      try {
        const stateArg = args[1]?.state || args[0]?.state || latestState();
        const pc = parseInt(String(stateArg?.playerCount ?? selectedPlayerCount()), 10) || selectedPlayerCount();
        enforceTimelineCount(pc, 'post-updateui-c27');
        enforceActiveScorePanelForCurrentMode('post-updateui-c27');
      } catch (_) {}
      return result;
    };
    wrappedUpdateUI.__c27Wrapped = true;
    w.beatlineTsUpdateUI = wrappedUpdateUI;
  }

  const overlay = document.getElementById('start-overlay');
  if (overlay) {
    const obs = new MutationObserver(() => cleanupStartState('start-overlay-visible'));
    obs.observe(overlay, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  document.addEventListener('change', (event) => {
    const target = event.target as HTMLElement | null;
    if (target && target.matches && target.matches('input[name="p_count"], input[name="solo_mode"]')) {
      w.__beatlineNextGamePlayerCount = selectedCountFromControls();
      w.__beatlineNextGameSoloMode = selectedSoloFromControls();
      syncForceOneTimelineClasses('player-count-or-solo-change');
      if (selectedCountFromControls() === 1) hideInactiveSinglePlayerDom('player-count-or-solo-change');
      if (isStartOverlayVisible()) cleanupStartState('player-count-or-solo-change-on-start');
      [0, 40, 160, 400].forEach((ms) => window.setTimeout(() => {
        enforceTimelineCount(isStartOverlayVisible() ? 0 : selectedPlayerCount(), `change-count-${ms}`);
        enforceActiveScorePanelForCurrentMode(`change-score-${ms}`);
      }, ms));
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const leaveHit = target.closest?.('#btn-leave, #confirm-leave, [data-action="leave"], .leave-game, .btn-leave-game, .settings-leave, .menu-leave');
    const startHit = target.closest?.('#start-game, #btn-start, .start-game-btn');
    if (startHit) { captureNextStartSelection(); if (selectedCountFromControls() === 1) hideInactiveSinglePlayerDom('start-click-force-one'); }
    if (leaveHit) setForcedPlayerCount(0, 3500);
    if (leaveHit || startHit) {
      [0, 40, 120, 320, 800, 1600, 2600].forEach((ms) => window.setTimeout(() => {
        if (leaveHit) cleanupStartState(`leave-click-${ms}`);
        enforceTimelineCount(selectedPlayerCount(), `${leaveHit ? 'leave-click' : 'start-click'}-count-${ms}`);
        enforceActiveScorePanelForCurrentMode(`${leaveHit ? 'leave-click' : 'start-click'}-score-${ms}`);
      }, ms));
    }
  }, true);

  if (isNativeApp(config)) {
    window.addEventListener('focus', () => { cleanupStartState('native-focus'); enforceActiveScorePanelForCurrentMode('native-focus'); }, true);
    document.addEventListener('visibilitychange', () => { cleanupStartState('visibilitychange'); enforceActiveScorePanelForCurrentMode('visibilitychange'); }, true);
    window.setInterval(() => {
      enforceTimelineCount(selectedCountFromControls() === 1 ? 1 : selectedPlayerCount(), 'native-safety-interval');
      enforceActiveScorePanelForCurrentMode('native-safety-interval');
      hideInactiveSinglePlayerDom('native-safety-interval');
    }, 350);
  }
}

function spotifyTrackId(raw: unknown): string {
  const value = String(raw || '').trim();
  if (!value) return '';
  const uriMatch = value.match(/spotify:track:([A-Za-z0-9]+)/);
  if (uriMatch) return uriMatch[1];
  const urlMatch = value.match(/open\.spotify\.com\/track\/([A-Za-z0-9]+)/);
  if (urlMatch) return urlMatch[1];
  return value.replace(/^spotify:track:/, '').replace(/[^A-Za-z0-9]/g, '');
}

function getActivePlaylistIds(): string[] {
  const normalize = (items: any[]): string[] => items.map((x) => String(x || '').trim()).filter(Boolean);
  const state = latestState();
  if (Array.isArray(state?.playlistIds) && state.playlistIds.length) return normalize(state.playlistIds);
  if (state?.playlistId) return [String(state.playlistId)];

  const w = wAny();
  if (Array.isArray(w.currentPlaylistIds) && w.currentPlaylistIds.length) return normalize(w.currentPlaylistIds);
  if (w.currentPlaylistId) return [String(w.currentPlaylistId)];

  try {
    const raw = localStorage.getItem('beatline_selected_playlists_v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const ids = normalize(parsed);
        if (ids.length) return ids;
      }
    }
  } catch (_) {}

  const select = document.getElementById('playlist-select') as HTMLSelectElement | null;
  if (select?.value) return [String(select.value)];
  const onlineSelect = document.getElementById('playlist-select-online') as HTMLSelectElement | null;
  if (onlineSelect?.value) return [String(onlineSelect.value)];
  const settings: Record<string, any> = (w.beatlineSettings || {}) as Record<string, any>;
  const active = settings.activePlaylistId || settings.active_id || settings.playlist_id;
  return active ? [String(active)] : [];
}

const directCommittedSpotifyIds = new Map<string, number>();

function buildHeardCommitPayload(trackIds: string[]): Record<string, any> {
  const playlistIds = getActivePlaylistIds();
  const payload: Record<string, any> = {
    mode: 'spotify',
    track_ids: trackIds,
    native_app: '1'
  };
  if (playlistIds.length) {
    payload.playlist_id = playlistIds[0];
    if (playlistIds.length > 1) payload.playlist_ids = playlistIds;
  }
  return payload;
}

async function commitHeardViaAjaxFallback(payload: Record<string, any>): Promise<any> {
  const settings = (wAny().beatlineSettings || {}) as Record<string, any>;
  const ajaxUrl = settings.ajaxUrl || wAny().ajaxUrl || '';
  const nonce = settings.nonce || wAny().ajaxNonce || '';
  if (!ajaxUrl || !nonce) throw new Error('ajaxUrl/nonce ontbreekt voor heard commit');
  const form = new FormData();
  form.append('action', 'beatline_heard_commit');
  form.append('nonce', String(nonce));
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((item) => form.append(`${key}[]`, String(item)));
    else if (value !== undefined && value !== null) form.append(key, String(value));
  });
  const response = await fetch(ajaxUrl, { method: 'POST', body: form, credentials: 'include', cache: 'no-store' });
  const text = await response.text();
  try { return JSON.parse(text); }
  catch (_) { return { success: false, data: { message: 'Server gaf geen JSON terug.', raw: text.slice(0, 200) } }; }
}

async function commitSpotifyTrackDirect(trackId: string, reason: string): Promise<void> {
  const id = spotifyTrackId(trackId);
  if (!id) return;

  const now = Date.now();
  const last = directCommittedSpotifyIds.get(id) || 0;
  if (now - last < 8000) return;
  directCommittedSpotifyIds.set(id, now);

  const w = wAny();
  try { if (typeof w.beatlineHeardMarkPlayed === 'function') w.beatlineHeardMarkPlayed(id); } catch (_) {}

  const payload = buildHeardCommitPayload([id]);
  if (!payload.playlist_id && !payload.playlist_ids) {
    if (w.BEATLINE_VITE_DEBUG) console.warn('[BeatLine Vite C40] Spotify heard commit skipped: playlist ontbreekt', { id, reason, payload });
    return;
  }

  let resp: any = null;
  let method = '';
  try {
    const commit = w.beatlineHeardCommit;
    if (typeof commit === 'function') {
      method = 'window.beatlineHeardCommit';
      resp = await commit(payload);
    } else {
      method = 'ajax-fallback';
      resp = await commitHeardViaAjaxFallback(payload);
    }

    if (!resp || resp.success !== true || resp?.data?.logged_in === false) {
      method = 'ajax-fallback-retry';
      resp = await commitHeardViaAjaxFallback(payload);
    }

    try {
      w.__beatlineC27LastSpotifyHeardCommit = { time: new Date().toISOString(), id, reason, method, payload, response: resp };
      localStorage.setItem('beatline_c27_last_spotify_heard_commit', JSON.stringify(w.__beatlineC27LastSpotifyHeardCommit));
    } catch (_) {}

    if (w.BEATLINE_VITE_DEBUG) {
      console.info('[BeatLine Vite C40] Spotify heard commit', {
        id,
        reason,
        method,
        payload,
        success: !!resp?.success,
        loggedIn: resp?.data?.logged_in,
        heardCount: resp?.data?.heard_count,
        response: resp,
      });
    }
    window.dispatchEvent(new CustomEvent('beatline_spotify_heard_committed', { detail: { id, reason, method, response: resp } }));
    try { window.dispatchEvent(new CustomEvent('beatline_profile_stats_dirty')); } catch (_) {}
  } catch (error) {
    try {
      w.__beatlineC27LastSpotifyHeardError = { time: new Date().toISOString(), id, reason, payload, error: String((error as any)?.message || error) };
      localStorage.setItem('beatline_c27_last_spotify_heard_error', JSON.stringify(w.__beatlineC27LastSpotifyHeardError));
    } catch (_) {}
    if (w.BEATLINE_VITE_DEBUG) console.warn('[BeatLine Vite C40] Spotify heard commit failed', { id, reason, payload, error });
  }
}

function stateTrackId(): string {
  const state = latestState();
  const activeCard = state?.activeCard || {};
  const track = state?.activeTrack || state?.currentTrack || state?.track || activeCard || {};
  const candidates = [
    activeCard.id_spotify, activeCard.spotify_id, activeCard.spotifyId, activeCard.spotifyTrackId, activeCard.spotify_uri, activeCard.spotifyUri, activeCard.uri, activeCard.trackUri, activeCard.id,
    track.spotify_id, track.spotifyId, track.spotifyTrackId, track.spotify_uri, track.spotifyUri, track.uri, track.trackUri, track.id,
    state?.activeTrackId, state?.activeCardId
  ];
  for (const candidate of candidates) {
    const id = spotifyTrackId(candidate);
    if (id) return id;
  }
  return '';
}

function spotifyPreferred(): boolean {
  const w = wAny();
  try {
    const raw = localStorage.getItem('beatline_playback_mode') || localStorage.getItem('beatline_spotify_playback_mode');
    if (raw === 'spotify') return true;
  } catch (_) {}
  const state = latestState();
  return String(state?.mode || '').toLowerCase() === 'spotify' || !!w.BeatLineSpotify;
}

function installNativeSpotifyHeardFix(config: BeatLineViteConfig): void {
  if (!isNativeApp(config)) return;
  const w = wAny();
  if (w.__beatlineC27SpotifyHeardFix) return;
  w.__beatlineC27SpotifyHeardFix = true;

  w.beatlineC27CommitSpotifyHeardNow = function(reason = 'manual') {
    const id = stateTrackId();
    if (id) return commitSpotifyTrackDirect(id, String(reason));
    return Promise.resolve();
  };
  // Called from the classic legacy runtime where playlist closure variables are available.
  w.beatlineC27CommitSpotifyHeardTrack = function(trackId: string, reason = 'legacy-playMusic') {
    return commitSpotifyTrackDirect(trackId, String(reason));
  };

  const wrapNativeHandler = () => {
    const current = w.beatlineHandleNativeSpotifyState;
    if (typeof current !== 'function' || (current as any).__c27Wrapped) return;
    const wrapped = function(this: any, payload: any) {
      const result = current.apply(this, [payload]);
      try {
        const type = String(payload?.type || '').toLowerCase();
        const raw = payload?.uri || payload?.trackUri || payload?.track_id || payload?.trackId || payload?.id || '';
        if (raw && (type === 'playing' || type === 'remote_play_starting' || type === 'playback_started')) {
          window.setTimeout(() => commitSpotifyTrackDirect(raw, `native-state-${type}`), 250);
        } else if (type === 'playing') {
          window.setTimeout(() => w.beatlineC27CommitSpotifyHeardNow('native-state-playing-no-uri'), 350);
        }
      } catch (_) {}
      return result;
    };
    wrapped.__c27Wrapped = true;
    w.beatlineHandleNativeSpotifyState = wrapped;
  };

  const wrapBridge = () => {
    const bridge = w.BeatLineSpotify;
    if (!bridge || typeof bridge.playTrack !== 'function' || (bridge.playTrack as any).__c27Wrapped) return;
    const original = bridge.playTrack;
    const wrapped = function(this: any, trackUri: string, ...rest: any[]) {
      const result = original.apply(this, [trackUri, ...rest]);
      window.setTimeout(() => commitSpotifyTrackDirect(trackUri, 'native-bridge-playTrack-c27'), 700);
      return result;
    };
    wrapped.__c27Wrapped = true;
    bridge.playTrack = wrapped;
  };

  [0, 150, 350, 750, 1500, 3000, 6000].forEach((ms) => window.setTimeout(() => {
    wrapNativeHandler();
    wrapBridge();
  }, ms));

  let lastStateTrack = '';
  const poll = window.setInterval(() => {
    wrapNativeHandler();
    wrapBridge();
    if (!spotifyPreferred()) return;
    const id = stateTrackId();
    if (id && id !== lastStateTrack) {
      lastStateTrack = id;
      window.setTimeout(() => commitSpotifyTrackDirect(id, 'native-activeTrack-poll-c27'), 900);
    }
  }, 900);
  window.setTimeout(() => window.clearInterval(poll), 10 * 60 * 1000);

  window.addEventListener('beatline_spotify_native_play', (event: Event) => {
    const detail = (event as CustomEvent).detail || {};
    const raw = detail.uri || detail.trackUri || detail.id || detail.trackId || '';
    if (raw) commitSpotifyTrackDirect(raw, 'custom-event-native-play');
  });
}

function installNativeVersionVerifier(config: BeatLineViteConfig): void {
  const w = wAny();
  w.__beatlineViteRuntimeVersion = C40_VERSION;
  w.__beatlineC40RuntimeLoadedAt = new Date().toISOString();
  if (!isNativeApp(config)) return;
  try {
    localStorage.setItem('beatline_native_runtime_version', C40_VERSION);
    localStorage.setItem('beatline_native_runtime_url', window.location.href);
  } catch (_) {}
}


export function installPostLegacyRuntimeFixes(config: BeatLineViteConfig): void {
  installNativeVersionVerifier(config);
  installLeaveResetHardening(config);
  installNativeSpotifyHeardFix(config);
}
